import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import OrganiserNeeds from '../../components/common/OrganiserNeeds';
import { apiErrorText, apiRequest, apiFormRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';

/* The simplified event editor — what an event IS, and nothing about running it.

   Deliberately short, twice over. An Event carries dozens of columns; all but these are brand
   setup (ticket types, scoring, mail senders, whitelabeling) and a customer has no business near
   them. What is left is then split again, and that split is the point of this screen: here is
   the name, the hour, the room and the picture — the things whoever stands in front of the room
   needs right. Everything that belongs to the COMPANY rather than to the event — its categories,
   what the event is FOR, whether registration is open, who is coming, publishing, cancelling,
   deleting — is on the run screen's settings, one button away at the top.

   ★ That is why an EXTERNAL host opens this screen and not that one. A facilitator we send in
   fixes the address and the description; they never see the attendance list, and `can_run` — the
   server's own answer to the run screen's gate — is what decides whether the button is even
   drawn. See routes/company_events._edit_gate.

   ★ ONLY a future event. A past one is the record of something that happened — the mails name
   its hour, the run sheet is what people worked to, the answers are stamped with it — so the
   whole form locks the day after and Machina is where it still changes. The server refuses it
   too; this screen just stops offering.

   ★ Times are WALL CLOCK in the event's own timezone, both ways. The server converts to and from
   UTC using the timezone in the same payload, so changing the zone and the hour in one save does
   the right thing.

   ★ Where it is comes out of an ADDRESS BOOK. A company runs its year in the same handful of
   places, so the venue, the directions and the map are kept once per company
   (data.EventLocation) and picking one COPIES all three onto the event. Saving an event with a
   place the book does not know adds it — the book grows by being used. "Our locations" below
   the fields is where it is curated, and only the admin gets it.

   ★ Saving here NEVER mails anybody. The API would, on a change to the date or the place, and
   this screen turns that off explicitly rather than by leaving the flag out — the default is to
   send. Telling the company is a decision, and it is made on the run screen, next to publishing.
   That is also what makes the editor safe to open at 23:00 to fix a typo. */

const ZONES = (() => {
  try {
    // Available in every browser we support; the fallback keeps the field usable if not.
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['Europe/Brussels', 'Europe/London', 'Asia/Dubai', 'America/New_York', 'UTC'];
  }
})();

// What this screen sends back. The registration switches are NOT here on purpose: they are the
// run screen's, and a field posted from both screens is a field the older tab quietly reverts.
const FIELDS = ['name', 'time_zone', 'date', 'end_date', 'closing_date', 'short_desc', 'is_online',
                'meeting_url', 'is_external_event', 'external_url',
                'location_name', 'location_desc', 'location_embed'];

const CompanyEventEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  // What the form looked like when it was loaded or last saved. Only used to answer "would
  // leaving this screen throw something away" — the address book is one navigation away.
  const [baseline, setBaseline] = useState(null);
  // ★ OFF by default, even for a series. An edit lands on the date you opened, and nowhere
  // else, unless you tick the box — quietly rewriting nine other dates is the change nobody
  // can take back, so widening it stays a decision you make on purpose.
  const [applySeries, setApplySeries] = useState(false);
  const [busy, setBusy] = useState(false);
  // Seeded from whatever the screen that sent us here had to say and no longer had a screen to
  // say it on — "three events created", from the programme's create form, which now lands you
  // in the first of them rather than back on its own list.
  const [notice, setNotice] = useState((location.state && location.state.notice) || null);

  // May this person also RUN the event? The server's answer to the OTHER screen's gate, so the
  // button never leads into a 403 — and an external host, who may edit and not run, sees no
  // button rather than a disabled one.
  const canRun = !!(data && data.can_run);
  // Does a COMPANY own this event? A public Bravos Lab does not, and part of this screen is about
  // things only a company has — somebody else's event, the address book of its own venues. Read
  // off the payload rather than inferred from those lists being empty: an empty picker reads as
  // "you have not set any up yet", which is a different sentence.
  const isCompanyEvent = !!(data && data.is_company_event);
  // Already happened → read it, do not change it. Day granularity, so today's event is still
  // editable while you are standing in the room.
  const locked = !!(data && data.event && data.event.is_past);
  // Where "back" goes. The programme list is the ADMIN's screen; a host of any kind reached this
  // event from "Events I'm hosting", and sending them to a 403 is not a way out.
  const backTo = (data && data.can_manage)
    ? ((data.event && data.event.programme_id)
      ? `/programme/${data.event.programme_id}` : '/programmes')
    : '/my-lab';
  // Is there anything on this screen that leaving it would throw away? Asked by the one button
  // that navigates away mid-edit.
  const dirty = !!(form && baseline && JSON.stringify(form) !== JSON.stringify(baseline));

  const adopt = useCallback((event) => {
    const next = {};
    FIELDS.forEach((f) => { next[f] = event[f] == null ? '' : event[f]; });
    setForm(next);
    setBaseline(next);
  }, []);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}`)
      .then((res) => { setData(res); adopt(res.event); })
      // The programme list is the admin's screen and belongs to a company; somebody who cannot
      // open this editor may have no company at all, so the event's own page is the way back.
      .catch(() => navigate(`/public-event/${eventId}`));
  }, [eventId, navigate, adopt]);

  useEffect(() => { load(); }, [load]);

  const event = data && data.event;
  const zones = useMemo(() => {
    // Keep whatever the event already carries even when the browser does not list it, or
    // opening the editor would silently move the event to the first zone in the list.
    const current = (event && event.time_zone) || '';
    return current && !ZONES.includes(current) ? [current, ...ZONES] : ZONES;
  }, [event]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = () => {
    if (busy || !form) return;
    setBusy(true);
    setNotice(null);
    // ★ notify: false, spelled out. The API defaults it to TRUE, so dropping the field would
    // mail every registered person the moment somebody corrects an hour.
    apiRequest('PUT', `/company/event/${eventId}`,
               { ...form, notify: false, apply_to_series: applySeries })
      .then((res) => {
        setData((d) => ({ ...d, event: res.event }));
        adopt(res.event);
        setNotice(res.told ? t('toldRegistered', { n: res.told }) : t('saved'));
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const uploadBanner = (file) => {
    if (!file || busy) return;
    setBusy(true);
    setNotice(null);
    const body = new FormData();
    body.append('banner', file);
    apiFormRequest(`/company/event/${eventId}/banner`, body)
      .then((res) => setData((d) => ({ ...d, event: { ...d.event, banner: res.banner } })))
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  // Picking a place COPIES its three fields onto the event — there is no link back to the book,
  // so a past event never moves when somebody tidies it up. The select itself stays on its
  // placeholder: it is a way to fill the fields below, not a value the event carries.
  const pickLocation = (id) => {
    const found = (data.locations || []).find((l) => l.id === id);
    if (!found) return;
    setForm((f) => ({ ...f,
                      location_name: found.name || '',
                      location_desc: found.description || '',
                      location_embed: found.embed || '' }));
  };

  const openLocations = () => {
    // eslint-disable-next-line no-alert
    if (dirty && !window.confirm(t('leaveUnsaved'))) return;
    navigate('/our-locations');
  };

  if (data === null || form === null) {
    return (
      <Base activeNav="home">
        <Card><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  return (
    <Base activeNav="home">
      <Card>
        <Back to={backTo} />
        <Title my="1" className="mt-3">
          <Icon name="calendar" theme="primary" className="me-2" />
          {event.name}
        </Title>

        {locked && <p className="ce-note ce-note--warn">{t('eventIsPast')}</p>}

        {/* One fieldset over the lot rather than `disabled` on twenty inputs: the rule is about
            the event, not about any one field, and a screen that locks half of itself is how a
            field gets forgotten the next time one is added. */}
        <fieldset className="ce-lock" disabled={locked}>
        {/* The picture and the three fields that name the event, side by side. Square and half a
            column rather than a strip across the whole card: it is the event's face, not its
            masthead, and full-width it was the biggest thing on a screen where it matters least.
            The fields beside it stack, one under the other, so the pair reads as one block. */}
        <div className="ce-identity">
          <div className="ce-identity__pic">
            <div
              className="ce-banner"
              style={event.banner ? { backgroundImage: `url("${event.banner}")` } : undefined}
            />
            <label className="bravos-form__label">
              {t('eventBanner')}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => uploadBanner(e.target.files[0])}
              />
            </label>
          </div>

          <div className="bravos-form ce-identity__fields">
            <span className="bravos-form__label">{t('eventName')}</span>
            <input
              type="text"
              value={form.name}
              maxLength={50}
              onChange={(e) => set('name', e.target.value)}
            />
            <label className="bravos-form__label">
              {t('startsAt')}
              <input
                type="datetime-local"
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
              />
            </label>
            <label className="bravos-form__label">
              {t('endsAt')}
              <input
                type="datetime-local"
                value={form.end_date}
                onChange={(e) => set('end_date', e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="bravos-form">
          <label className="bravos-form__label">
            {t('timeZone')}
            <select value={form.time_zone} onChange={(e) => set('time_zone', e.target.value)}>
              {zones.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </label>
          <p className="ce-note">{t('multiDayHint')}</p>

          {/* ★ Event.closing_date — the same column Machina calls "Final editing date" and that
              Hercules Trophy already locks team edits on. After it nobody can book AND nobody
              can cancel: by then the host has committed to a headcount, and a late withdrawal
              costs them as much as a late booking. An admin can still seat someone by hand. */}
          <label className="bravos-form__label">
            {t('closingDate')}
            <input
              type="datetime-local"
              value={form.closing_date}
              onChange={(e) => set('closing_date', e.target.value)}
            />
          </label>
          <p className="ce-note">
            {t('closingDateHint')}
            {event.registration_closed ? ` ${t('registrationClosed')}` : ''}
          </p>

          <span className="bravos-form__label">{t('description')}</span>
          <textarea
            rows={3}
            maxLength={1024}
            value={form.short_desc}
            onChange={(e) => set('short_desc', e.target.value)}
          />

          {/* What it is FOR — the company's own themes, values and UN goals — is NOT here. It is
              the same kind of answer as which KIND of event this is: the company's vocabulary
              rather than this event's words, so both chip rows live together on the run screen's
              settings, where an external host does not reach them. */}
        </div>
        </fieldset>
      </Card>

      <Card>
        <Title my="1" className="mt-2">
          <Icon name="marker" theme="primary" className="me-2" />
          {t('whereIsIt')}
        </Title>
        <fieldset className="ce-lock" disabled={locked}>
        <div className="bravos-form">
          <label className="ce-check">
            <input
              type="checkbox"
              checked={!!form.is_online}
              onChange={(e) => set('is_online', e.target.checked)}
            />
            <span>{t('onlineEvent')}</span>
          </label>

          {/* An event somebody ELSE runs — a trade fair, an industry conference, a partner's
              summit. It belongs here, with the venue, because that is the question it answers:
              where is this thing, and whose room is it. And it is a SEPARATE tick from "online",
              never a third value of it — somebody else's event can be a hall or a webinar, and
              the box above still says which.

              Only where a company owns the event. A public Bravos Lab is the brand's own, so
              there is nobody else for it to belong to. */}
          {isCompanyEvent && (
            <>
              <label className="ce-check">
                <input
                  type="checkbox"
                  checked={!!form.is_external_event}
                  onChange={(e) => set('is_external_event', e.target.checked)}
                />
                <span>
                  {t('externalEvent')}
                  <span className="ce-check__hint">{t('externalEventHint')}</span>
                </span>
              </label>
              {/* The organiser's own page. It is what a colleague deciding whether to go will
                  want to read — the programme, the speakers, the price — and none of that is
                  ours to retype here. */}
              {form.is_external_event && (
                <>
                  <span className="bravos-form__label">{t('externalEventUrl')}</span>
                  <input
                    type="url"
                    value={form.external_url}
                    placeholder="https://…"
                    onChange={(e) => set('external_url', e.target.value)}
                  />
                  <p className="ce-note">{t('externalEventUrlHint')}</p>
                </>
              )}
            </>
          )}

          {form.is_online ? (
            <>
              <span className="bravos-form__label">{t('meetingLink')}</span>
              <input
                type="url"
                value={form.meeting_url}
                placeholder="https://…"
                onChange={(e) => set('meeting_url', e.target.value)}
              />
            </>
          ) : (
            <>
              {/* Where this company has held things before, most recently used first. Drawn only
                  when there is something to pick — an empty dropdown is worse than no dropdown,
                  and the book fills itself the first time an event is saved with a place. */}
              {(data.locations || []).length > 0 && (
                <>
                  <span className="bravos-form__label">{t('savedLocations')}</span>
                  <select value="" onChange={(e) => pickLocation(e.target.value)}>
                    <option value="">{t('pickSavedLocation')}</option>
                    {data.locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                        {l.used ? ` — ${t('usedNTimes', { n: l.used })}` : ''}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <span className="bravos-form__label">{t('location')}</span>
              <input
                type="text"
                value={form.location_name}
                maxLength={50}
                onChange={(e) => set('location_name', e.target.value)}
              />
              <span className="bravos-form__label">{t('howToGetThere')}</span>
              <textarea
                rows={2}
                maxLength={2500}
                value={form.location_desc}
                onChange={(e) => set('location_desc', e.target.value)}
              />
              <span className="bravos-form__label">{t('mapEmbed')}</span>
              <textarea
                rows={2}
                maxLength={2500}
                value={form.location_embed}
                placeholder="<iframe …>"
                onChange={(e) => set('location_embed', e.target.value)}
              />
            </>
          )}
        </div>
        </fieldset>

        {/* Outside the fieldset on purpose: the address book is the COMPANY's, not this event's,
            so a past event locking its own fields must not lock the way to it. The admin's, like
            the rest of what belongs to the company — a host edits where the event is, and does
            not rewrite where the company holds things. `isCompanyEvent` is said out loud even
            though `can_manage` is never true without one: a book belonging to nobody is not a
            thing to leave depending on another flag's side effect. */}
        {isCompanyEvent && data.can_manage && (
          <button
            type="button"
            className="bravos-submit bravos-submit--ghost"
            onClick={openLocations}
          >
            <Icon name="marker" /> {t('ourLocations')}
          </button>
        )}
      </Card>

      {/* What the ORGANISER of somebody else's event needs from our people — the ticks that
          become columns in the participant sheet, and the questions that get asked at
          registration. Its own card rather than more of "Where is it?": it appears the moment
          the external tick goes on, right under the card you ticked it in, and the venue card
          stays readable.

          ★ `can_manage`, not `can_edit`. This screen also admits the 'edit' rung — a facilitator
          we send in — and deciding which of our people's data leaves the building is not theirs.
          It is the same line that keeps the guest list off this screen.

          ★ Drawn on the UNSAVED tick, so it appears the instant you decide. The block saves
          itself as you tick; the consequence to know is that picking fields and then leaving
          without saving the form keeps the picks on an event that is not marked external yet.
          Harmless — the table is deliberately event-agnostic and the export simply carries
          those columns — and the alternative is a block you cannot reach until after a save. */}
      {isCompanyEvent && data.can_manage && form.is_external_event && (
        <Card>
          <Title my="1">
            <Icon name="clipboard-list" theme="primary" className="me-2" />
            {t('organiserNeeds')}
          </Title>
          <OrganiserNeeds eventId={eventId} />
        </Card>
      )}

      {/* One save. Publishing, cancelling and deleting an event are not editing it — they are
          decisions about whether it happens, and they are on the run screen with the rest of
          what belongs to the company, along with the choice to tell anybody about it. */}
      {(!locked || canRun) && (
      <Card>
        {!locked && event.series_size > 1 && (
          <label className="ce-check">
            <input
              type="checkbox"
              checked={applySeries}
              onChange={(e) => setApplySeries(e.target.checked)}
            />
            <span>
              {t('applyToSeries', { n: event.series_size })}
              <span className="ce-check__hint">{t('applyToSeriesHint')}</span>
            </span>
          </label>
        )}
        {/* Save, and the way to the other half of the event beside it. Beside rather than at
            the top: this is the end of the form, which is where you are standing when you are
            done editing and ready to go and run the thing. */}
        <div className="ce-actions">
          {!locked && (
            <button type="button" className="bravos-submit" onClick={save} disabled={busy}>
              {busy ? t('pleaseWait') : t('save')}
            </button>
          )}
          {canRun && (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => navigate(`/host-event/${eventId}`)}
            >
              <Icon name="megaphone" /> {t('hostEvent')}
            </button>
          )}
          {/* Who is coming, and the sheet you send the organiser. ``can_manage`` and not
              ``can_run``, because that is exactly who the People screen lets in — it is
              ``_bravos_engagement_gate``, so a host of any rung would land on a 404. Until now
              that screen was reachable only by going through "Run this event" first. */}
          {data.can_manage && (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => navigate(`/company-event/${eventId}/people`)}
            >
              <Icon name="user-check" /> {t('whoIsComing')}
            </button>
          )}
          {/* Same gate as running it: what kind of competition this event is belongs to the
              people who own the day, not to a facilitator we sent in. An event with no
              competition set up is every event that exists today, so this is a door, not a
              step — nothing here is required to run an ordinary event. */}
          {canRun && (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => navigate(`/company-event/${eventId}/tournament`)}
            >
              <Icon name="flag-checkered" /> {t('tournamentTitle')}
            </button>
          )}
        </div>

        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>
      )}
    </Base>
  );
};

export default CompanyEventEdit;
